// Safely handle Capacitor imports - may not be available in Docker/web environments
let Capacitor: any = null;
let StatusBar: any = null;
let Style: any = null;
let SplashScreen: any = null;

try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const capacitorCore = require('@capacitor/core');
  Capacitor = capacitorCore.Capacitor;

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const statusBarModule = require('@capacitor/status-bar');
  StatusBar = statusBarModule.StatusBar;
  Style = statusBarModule.Style;

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const splashScreenModule = require('@capacitor/splash-screen');
  SplashScreen = splashScreenModule.SplashScreen;
} catch (error) {
  // Capacitor modules not available - running in web mode
}

/**
 * Capacitor utility functions for platform detection and native plugin initialization
 */

/**
 * Check if the app is running on a native platform (iOS or Android)
 */
export const isNativePlatform = (): boolean => {
  try {
    return Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform();
  } catch (error) {
    return false;
  }
};

/**
 * Get the current platform name
 * Returns: 'web', 'ios', or 'android'
 */
export const getPlatform = (): string => {
  try {
    return Capacitor && Capacitor.getPlatform ? Capacitor.getPlatform() : 'web';
  } catch (error) {
    return 'web';
  }
};

/**
 * Check if running on iOS
 */
export const isIOS = (): boolean => {
  return getPlatform() === 'ios';
};

/**
 * Check if running on Android
 */
export const isAndroid = (): boolean => {
  return getPlatform() === 'android';
};

/**
 * Check if running on web
 */
export const isWeb = (): boolean => {
  return getPlatform() === 'web';
};

/**
 * Initialize native app features
 * Call this once when the app starts
 */
export const initializeApp = async (): Promise<void> => {
  if (!isNativePlatform()) {
    // console.log('Running on web, skipping native initialization');
    return;
  }

  try {
    // Hide splash screen after app is ready
    if (SplashScreen) {
      await SplashScreen.hide();
    }

    // Configure status bar
    await configureStatusBar();

    // Configure keyboard (if needed)
    await configureKeyboard();

    // console.log(`App initialized on ${getPlatform()}`);
  } catch (error) {
    // console.error('Error initializing native features:', error);
  }
};

/**
 * Configure the status bar appearance
 */
export const configureStatusBar = async (): Promise<void> => {
  if (!isNativePlatform() || !StatusBar || !Style) return;

  try {
    // Set status bar style (light content for dark background)
    await StatusBar.setStyle({ style: Style.Light });

    // Set status bar background color (matches app theme)
    if (isAndroid()) {
      await StatusBar.setBackgroundColor({ color: '#4f46e5' });
    }

    // console.log('Status bar configured');
  } catch (error) {
    // console.error('Error configuring status bar:', error);
  }
};

/**
 * Configure keyboard behavior
 * Note: Import Keyboard from '@capacitor/keyboard' if you need to add listeners
 */
export const configureKeyboard = async (): Promise<void> => {
  if (!isNativePlatform()) return;

  try {
    // You can add keyboard event listeners here if needed
    // Example:
    // import { Keyboard } from '@capacitor/keyboard';
    // Keyboard.addListener('keyboardWillShow', (info) => {
    //   // console.log('Keyboard will show', info);
    // });
    // console.log('Keyboard configured');
  } catch (error) {
    // console.error('Error configuring keyboard:', error);
  }
};

/**
 * Show the splash screen (useful for transitions)
 */
export const showSplash = async (): Promise<void> => {
  if (!isNativePlatform() || !SplashScreen) return;

  try {
    await SplashScreen.show({
      showDuration: 2000,
      autoHide: true,
    });
  } catch (error) {
    // console.error('Error showing splash screen:', error);
  }
};

/**
 * Hide the splash screen manually
 */
export const hideSplash = async (): Promise<void> => {
  if (!isNativePlatform() || !SplashScreen) return;

  try {
    await SplashScreen.hide();
  } catch (error) {
    // console.error('Error hiding splash screen:', error);
  }
};

/**
 * Get platform-specific class names for conditional styling
 */
export const getPlatformClasses = (): string => {
  const platform = getPlatform();
  return `platform-${platform} ${isNativePlatform() ? 'is-native' : 'is-web'}`;
};

const capacitorUtils = {
  isNativePlatform,
  getPlatform,
  isIOS,
  isAndroid,
  isWeb,
  initializeApp,
  configureStatusBar,
  configureKeyboard,
  showSplash,
  hideSplash,
  getPlatformClasses,
};

export default capacitorUtils;
